import { Api } from 'src/apis/api';
import { Gtm } from 'src/apis/gtm';
import { ForageService } from 'src/services/ForageService';
import {
  ICreateKlarnaCustomerTokenPayload,
  IImportUpsaleKlarnStripePayload,
  IPpFeatureFlag,
  IPurchaseApplePayUpsellPayload,
  IPurchaseCreditCardUpsellPayload,
  IPurchaseGooglePayUpsellPayload,
  IPurchasePayPalUpsellPayload,
  PaySource,
} from 'src/types/api';
import { IProduct } from 'src/types/product';
import { runScript } from 'src/utils/eval';
import { updatePurchasedToCookie } from 'src/utils/products';
import { SiftEvents } from 'src/utils/siftEvents';

import { isCurrentProductTaken } from '../../utils/lead';
import { navigate } from '../../utils/navigation';
import { calculatePrices } from '../../utils/numbers';
import { getPromoFieldName } from '../../utils/upsell';
import { createUrl, getUrlParameters, removeUrlParams } from '../../utils/url';
import { isValidUrl } from '../../utils/validations';
import { FunnelController } from '../business/FunnelController';
import { UiController } from '../ui/UiController';

let NON_RESPONSIVE_TS: NodeJS.Timeout | null = null;

export class UpsellController {
  private fc: FunnelController;
  upsellKey: number;
  private ppConfig: IPpFeatureFlag = window.FLAGS.pp_profile as IPpFeatureFlag;

  constructor(fc: FunnelController, upsellKey: number) {
    this.fc = fc;
    this.upsellKey = upsellKey;
  }

  // TODO create getParams method

  private createPaypalUpsellOrder = async (
    paySource: PaySource,
    upsellKey?: number,
  ) => {
    const lead = this.fc.lead.lead;
    const product = this.fc.getSelectedProduct();

    const storage = ForageService.getInstance();
    await storage.setValue('selectedProductInstance', product);

    Gtm.trackUpsellButtonClick(
      paySource,
      lead.orderId,
      lead.customerId,
      lead.auid,
    );

    const upsellData: IPurchasePayPalUpsellPayload = {
      orderId: lead?.orderId || '',
      sessionId: lead.sessionId,
      client: window.BRAND,
      campaignId: this.fc.config.campaignId,
      paySource,
      product1_id: product.id,
      product1_price: product.finalPrice,
      product1_shipPrice: this.fc.config.shippingPrice || 0,
      salesUrl: location.href,
      errorRedirectsTo: location.href,
      redirectsTo: createUrl(this.fc.config.redirectTo || '', {
        orderId: lead.orderId || '',
      }),
      custom_order_sid: lead.sessionId,
      custom_order_auid: lead.auid || '',
      custom_order_paymentType: lead.paymentType || '',
      custom_order_paymentMethod: lead.paymentMethod || '',
      note:
        upsellKey !== undefined
          ? `${product?.isDownsell ? 'downsell' : 'upsell'}${upsellKey + 1}`
          : '',
    };
    const promoFieldName = getPromoFieldName(this.upsellKey);
    if (promoFieldName) {
      upsellData[promoFieldName] = String(product.id);
    }

    return await Api.importPaypalUpsell(upsellData);
  };

  private createStripeKlarnaUpsellOrder = async (upsellKey?: number) => {
    const lead = this.fc.lead.lead;
    const product = this.fc.getSelectedProduct();

    if (!this.fc.config?.redirectTo) {
      throw new Error('Error: redirectTo is undefined');
    }

    const upsellData: IImportUpsaleKlarnStripePayload = {
      orderId: lead?.orderId || '',
      sessionId: lead.sessionId,
      client: window.BRAND,
      campaignId: this.fc.config.campaignId,
      prepaidType: 'KLARNA',
      paySource: PaySource.PREPAID,
      product1_id: String(product.id),
      product1_price: product.finalPrice,
      product1_shipPrice: this.fc.config.shippingPrice || 0,
      forceMerchantId: this.fc.config.forceMerchantId || '117',
      salesUrl: location.href,
      errorRedirectsTo: location.href,
      redirectsTo: createUrl(this.fc.config.ppSuccessRedirectUrl || '', {
        redirectTo: this.fc.config.redirectTo,
      }),
      custom_order_sid: lead.sessionId,
      custom_order_auid: lead.auid || '',
      custom_order_paymentType: lead.paymentType || '',
      custom_order_paymentMethod: PaySource.STRIPE_KLARNA,
      note:
        upsellKey !== undefined
          ? `${product?.isDownsell ? 'downsell' : 'upsell'}${upsellKey + 1}`
          : '',
    };
    const promoFieldName = getPromoFieldName(this.upsellKey);
    if (promoFieldName) {
      upsellData[promoFieldName] = String(product.id);
    }

    return await Api.importKlarnaStripeUpsell(upsellData);
  };

  private createCreditCardUpsellOrder = async (upsellKey?: number) => {
    const lead = this.fc.lead.lead;
    const product = this.fc.getSelectedProduct();

    Gtm.trackUpsellButtonClick(
      PaySource.CREDITCARD,
      lead.orderId,
      lead.customerId,
      lead.auid,
    );

    if (!this.fc.config?.redirectTo) {
      throw new Error('Error: redirectTo is undefined');
    }

    const upsellData: IPurchaseCreditCardUpsellPayload = {
      errorRedirectsTo: location.href,
      redirectsTo: createUrl(this.fc.config.ppSuccessRedirectUrl || '', {
        redirectTo: this.fc.config.redirectTo,
      }),
      salesUrl: location.href,
      orderId: lead?.orderId || '',
      sessionId: lead.sessionId,
      client: window.BRAND,
      campaignId: this.fc.config.campaignId,
      paySource: PaySource.CREDITCARD,
      product1_id: product.id,
      product1_price: product.finalPrice,
      product1_shipPrice: this.fc.config.shippingPrice || 0,
      custom_order_sid: lead.sessionId,
      custom_order_auid: lead.auid || '',
      custom_order_paymentType: lead.paymentType || '',
      custom_order_paymentMethod: PaySource.CREDITCARD,
      note:
        upsellKey !== undefined
          ? `${product?.isDownsell ? 'downsell' : 'upsell'}${upsellKey + 1}`
          : '',
    };

    const promoFieldName = getPromoFieldName(this.upsellKey);
    if (promoFieldName) {
      upsellData[promoFieldName] = String(product.id);
    }

    return await Api.importCreditCardUpsell(upsellData);
  };

  private createApplePayUpsell = async (upsellKey?: number) => {
    const lead = this.fc.lead.lead;
    const product = this.fc.getSelectedProduct();

    Gtm.trackUpsellButtonClick(
      PaySource.APPLEPAY,
      lead.orderId,
      lead.customerId,
      lead.auid,
    );

    const upsellData: IPurchaseApplePayUpsellPayload = {
      emailAddress: lead.emailAddress,
      campaignId: this.fc.config.campaignId,
      orderId: lead?.orderId || '',
      product1_id: Number(product.id),
      product1_price: product.finalPrice,
      product1_shipPrice: product.shippingPrice,
      sessionId: lead.sessionId,
      paySource: PaySource.APPLEPAY,
      client: window.BRAND,
      custom_order_sid: lead.sessionId,
      custom_order_auid: lead.auid || '',
      custom_order_paymentMethod: PaySource.APPLEPAY,
      custom_order_paymentType: lead.paymentType || '',
      note:
        upsellKey !== undefined
          ? `${product?.isDownsell ? 'downsell' : 'upsell'}${upsellKey + 1}`
          : '',
    };
    const promoFieldName = getPromoFieldName(this.upsellKey);
    if (promoFieldName) {
      upsellData[promoFieldName] = String(product.id);
    }

    return await Api.importApplePayUpsell(upsellData);
  };

  private createGooglePayUpsell = async (upsellKey?: number) => {
    const lead = this.fc.lead.lead;
    const product = this.fc.getSelectedProduct();

    Gtm.trackUpsellButtonClick(
      PaySource.GOOGLEPAY,
      lead.orderId,
      lead.customerId,
      lead.auid,
    );

    const upsellData: IPurchaseGooglePayUpsellPayload = {
      emailAddress: lead.emailAddress,
      campaignId: this.fc.config.campaignId,
      orderId: lead?.orderId || '',
      client: window.BRAND,
      product1_id: product.id,
      product1_price: product.finalPrice,
      product1_shipPrice: product.shippingPrice,
      sessionId: lead.sessionId,
      paySource: PaySource.GOOGLEPAY,
      custom_order_sid: lead.sessionId,
      custom_order_auid: lead.auid || '',
      custom_order_paymentMethod: PaySource.GOOGLEPAY,
      custom_order_paymentType: lead.paymentType || '',
      note:
        upsellKey !== undefined
          ? `${product?.isDownsell ? 'downsell' : 'upsell'}${upsellKey + 1}`
          : '',
    };
    const promoFieldName = getPromoFieldName(this.upsellKey);
    if (promoFieldName) {
      upsellData[promoFieldName] = String(product.id);
    }

    return await Api.importGooglePayUpsell(upsellData);
  };

  private createKlarnaUpsellOrder = async (upsellKey?: number) => {
    const lead = this.fc.lead.lead;
    const product = this.fc.getSelectedProduct();

    Gtm.trackUpsellButtonClick(
      PaySource.KLARNA,
      lead.orderId,
      lead.customerId,
      lead.auid,
    );

    const redirect = createUrl(this.fc.config.redirectTo || '', {
      prospect_id: String(this.fc.lead.lead.orderId),
    });

    const { unitPrice, totalAmount } = calculatePrices(
      product.finalPrice,
      product.quantity,
      product.shippingPrice,
    );

    const isTaken = await isCurrentProductTaken(
      lead.orderId || '',
      String(product.id),
    );

    if (isTaken) {
      throw new Error('Upsale already taken');
    } else {
      const upsellData: ICreateKlarnaCustomerTokenPayload = {
        client: window.BRAND,
        klarnaPayload: {
          customerToken: String(lead.klarnaToken),
          locale: this.fc.config.locale || 'en',
          purchase_currency: product.currencyCode || 'EUR',
          order_amount: totalAmount,
          order_tax_amount: 0,
          order_lines: [
            {
              name: product.name,
              quantity: product.quantity,
              unit_price: unitPrice,
              tax_rate: 0,
              total_amount: unitPrice * product.quantity,
              total_tax_amount: 0,
              image_url: product.image,
            },
          ],
          merchant_urls: {
            confirmation: redirect,
          },
        },
        konnektivePayload: {
          orderId: String(lead.orderId),
          campaignId: String(this.fc.config.campaignId),
          product1_id: String(product.id),
          product1_price: product.finalPrice,
          product1_shipPrice: product.shippingPrice,
          paySource: PaySource.KLARNA,
          prepaidType: 'KLARNA',
          sessionId: lead.sessionId,
          currency: product.currencyCode || 'EUR',
          custom_order_sid: lead.sessionId,
          custom_order_auid: lead.auid || '',
          custom_order_paymentMethod: PaySource.KLARNA,
          custom_order_paymentType: lead.paymentType || '',
          note:
            upsellKey !== undefined
              ? `${product?.isDownsell ? 'downsell' : 'upsell'}${upsellKey + 1}`
              : '',
        },
      };
      const upsellCustomKey = getPromoFieldName(this.upsellKey);
      if (upsellCustomKey) {
        upsellData.konnektivePayload[upsellCustomKey] = String(product.id);
      }

      return await Api.createKlarnaCustomerTokenOrder(upsellData);
    }
  };

  createUpsellOrder = async (upsellKey?: number) => {
    console.log('UPSELL KEY: ' + upsellKey);
    const lead = this.fc.lead.lead;
    const product = this.fc.getSelectedProduct();

    UiController.showModalLoader(true);
    UiController.setUpsellButtonLoading(true);
    try {
      let res;
      if (!lead.paySource) {
        throw new Error('Error: lead does not have paySource set.');
      }
      if (!lead.orderId) {
        throw new Error('Error: order ID is not set.');
      }
      if (!product) {
        throw new Error('Error: Products is undefined');
      }

      const paySource = lead.paymentMethod || lead.paySource;

      if (paySource === PaySource.PREPAID) {
        throw new Error('User paySource was set to PREPAID in upsell page');
      }

      NON_RESPONSIVE_TS =
        Errors.maybeReportNonResponsivePaymentButton(paySource);
      if (paySource === PaySource.CREDITCARD) {
        res = await this.createCreditCardUpsellOrder(upsellKey);
      }

      if (paySource === PaySource.WITHREACH_PAYPAL) {
        res = await this.createPaypalUpsellOrder(
          PaySource.WITHREACH_PAYPAL,
          upsellKey,
        );
      }

      if (paySource === PaySource.PAYPAL) {
        res = await this.createPaypalUpsellOrder(PaySource.PAYPAL, upsellKey);
      }

      if (paySource === PaySource.STRIPE_KLARNA) {
        res = await this.createStripeKlarnaUpsellOrder(upsellKey);
      }

      if (paySource === PaySource.KLARNA) {
        res = await this.createKlarnaUpsellOrder(upsellKey);
      }

      if (paySource === PaySource.APPLEPAY) {
        res = await this.createApplePayUpsell(upsellKey);
      }

      if (paySource === PaySource.GOOGLEPAY) {
        res = await this.createGooglePayUpsell(upsellKey);
      }

      updatePurchasedToCookie(product, false, upsellKey);

      if (window.DEBUG) {
        console.log(res);
      }

      // Track the upsell in Sift using the update_order event
      if (res && res.orderId && product) {
        // Create a simple upsell data object for Sift tracking
        const upsellData = {
          product1_id: product.id,
          product1_name: product.name,
          product1_price: product.finalPrice,
          product1_qty: product.quantity || 1,
          paySource,
          custom_order_paymentMethod: paySource,
          currency_code: product.currencyCode || 'USD',
          // Include info that this is an upsell
          note: upsellKey !== undefined ? `upsell${upsellKey + 1}` : 'upsell',
        };

        // Track the upsell as an order update
        SiftEvents.updateOrder(lead, upsellData, res);

        // Also track the payment transaction with product details
        // Merge lead data with product details to ensure products are found
        const orderDataWithProduct = {
          ...lead,
          ...upsellData, // Include all product details
          orderId: lead.orderId,
          sessionId: lead.sessionId,
          emailAddress: lead.emailAddress,
          forceMerchantId: res?.items[0]?.merchantId, // Include merchant ID for gateway lookup
        };

        if (window.DEBUG) {
          console.log(
            '[Sift Debug] trackTransaction for upsell with enriched data:',
            {
              orderDataSample: Object.keys(orderDataWithProduct),
              productIncluded: !!orderDataWithProduct.product1_id,
              merchantId: orderDataWithProduct.forceMerchantId,
            },
          );
        }

        // Pass payment source directly to trackTransaction (it will map internally)
        SiftEvents.trackUpsellTransaction(
          orderDataWithProduct,
          res,
          {
            paymentType: paySource, // The SiftEvents class will map this to the correct format
          },
          true,
        );
      }

      if (!this.fc.config?.redirectTo) {
        throw new Error('Error: redirectTo is undefined');
      }

      if (
        paySource === PaySource.CREDITCARD &&
        res.url &&
        isValidUrl(res.url)
      ) {
        window.location.href = res.url;
        return;
      }

      if (
        paySource === PaySource.PAYPAL &&
        res.paypalUrl &&
        isValidUrl(res.paypalUrl)
      ) {
        window.location.href = res.paypalUrl;
        return;
      }

      if (
        paySource === PaySource.STRIPE_KLARNA &&
        res.url &&
        isValidUrl(res.url)
      ) {
        window.location.href = res.url;
        return;
      }

      if (paySource === PaySource.CREDITCARD && res.script) {
        runScript(res.script);
        return;
      }

      navigate(this.fc.config.redirectTo);
    } catch (e) {
      Errors.report(e, {
        geo: window.GEO,
        upsellKey: this.upsellKey,
        lead: lead || 'undefined',
        product: product || 'undefined',
        config: this.fc.config || 'undefined',
        paySource: lead.paySource,
      });
    } finally {
      NON_RESPONSIVE_TS && clearTimeout(NON_RESPONSIVE_TS);
    }
    setTimeout(() => {
      UiController.showModalLoader(false);
      UiController.setUpsellButtonLoading(false);
    }, 3000);
  };

  confirmPayPalOrder = async (redirectTo: string) => {
    const lead = this.fc.lead.lead;
    const params = getUrlParameters();
    const ppToken = params['token'];
    const baToken = params['ba_token'];
    const confirmed = params['paypalAccept']?.toLowerCase() === '1';
    const payerId = params['PayerID'];

    removeUrlParams(['token', 'ba_token', 'paypalAccept']);

    const storage = ForageService.getInstance();
    const [paypalMid, sessionId, productId] = await Promise.all([
      storage.getValue<string>('express_paypal_mid'),
      storage.getValue<string>('express_session_id'),
      storage.getValue<string>('express_product_id'),
    ]);

    const referrer = window.document.referrer;
    const hasAllParams = payerId && ppToken && lead.sessionId;
    if (window.DEBUG) {
      console.log('confirmPayPalOrder:', {
        ppToken,
        baToken,
        confirmed,
        paypalMid,
        sessionId,
        productId,
        params,
        referrer,
      });
    }

    if (referrer.includes('paypal.com') && confirmed && !hasAllParams) {
      const message = `PayPalError: Express missing params: paypalMid: ${paypalMid}, sessionId: ${sessionId}, productId: ${productId}`;
      Errors.report(
        new Error(message),
        {
          paypalMid,
          sessionId,
          productId,
          referrer,
        },
        false,
        false,
      );
      return;
    }

    await Promise.all([
      storage.deleteValue('express_paypal_mid'),
      storage.deleteValue('express_session_id'),
      storage.deleteValue('express_product_id'),
    ]);

    if (payerId && ppToken && lead.sessionId) {
      try {
        UiController.showModalLoader(true);
        UiController.setUpsellButtonLoading(true);

        const res = await this.sendUpsaleAfterPaypalConfirmation(
          PaySource.PAYPAL,
          this.upsellKey,
          ppToken,
          confirmed,
          payerId,
        );

        // Track the PayPal upsell in Sift
        if (res && res.orderId) {
          const storageInstance = ForageService.getInstance();
          const product = await storageInstance.getValue<IProduct>(
            'selectedProductInstance',
          );

          if (product) {
            // Create upsell data for Sift tracking
            const upsellData = {
              product1_id: product.id,
              product1_name: product.name || '',
              product1_price: product.finalPrice,
              product1_qty: product.quantity || 1,
              paySource: PaySource.PAYPAL,
              custom_order_paymentMethod: PaySource.PAYPAL,
              currency_code: product.currencyCode || 'USD',
              // Include info that this is an upsell
              note:
                this.upsellKey !== undefined
                  ? `upsell${this.upsellKey + 1}`
                  : 'upsell',
            };

            // Track the upsell as an order update
            SiftEvents.updateOrder(lead, upsellData, res);

            // Also track the payment transaction with product details
            // Merge lead data with product details to ensure products are found
            const orderDataWithProduct = {
              ...lead,
              ...upsellData, // Include all product details
              orderId: lead.orderId,
              sessionId: lead.sessionId,
              emailAddress: lead.emailAddress,
              forceMerchantId: res.merchantId, // Include merchant ID for gateway lookup
            };

            if (window.DEBUG) {
              console.log(
                '[Sift Debug] trackTransaction for PayPal upsell with enriched data:',
                {
                  orderDataSample: Object.keys(orderDataWithProduct),
                  productIncluded: !!orderDataWithProduct.product1_id,
                  merchantId: orderDataWithProduct.forceMerchantId,
                },
              );
            }

            // Pass payment source directly (will be mapped internally in SiftEvents)
            SiftEvents.trackUpsellTransaction(
              orderDataWithProduct,
              res,
              {
                paymentType: PaySource.PAYPAL,
              },
              true,
            );
          }
        }

        navigate(redirectTo || this.fc.config.redirectTo || '');
      } catch (e) {
        UiController.showModalLoader(false);
        UiController.setUpsellButtonLoading(false);

        Errors.report(e, {
          geo: window.GEO,
          upsellKey: this.upsellKey,
          lead: lead || 'undefined',
          config: this.fc.config || 'undefined',
          paySource: lead.paySource,
        });
      } finally {
        NON_RESPONSIVE_TS && clearTimeout(NON_RESPONSIVE_TS);
      }
    }
  };

  sendUpsaleAfterPaypalConfirmation = async (
    paySource: PaySource,
    upsellKey?: number,
    ppToken?: string,
    confirmed?: boolean,
    payerId?: string,
  ) => {
    const lead = this.fc.lead.lead;

    Gtm.trackUpsellButtonClick(
      paySource,
      lead.orderId,
      lead.customerId,
      lead.auid,
    );
    const storage = ForageService.getInstance();
    const product = await storage.getValue<IProduct>('selectedProductInstance');

    const upsellData: IPurchasePayPalUpsellPayload = {
      orderId: lead?.orderId || '',
      sessionId: lead.sessionId,
      client: window.BRAND,
      campaignId: this.fc.config.campaignId,
      paySource,
      product1_id: product.id,
      product1_price: product.finalPrice,
      product1_shipPrice: this.fc.config.shippingPrice || 0,
      salesUrl: location.href,
      errorRedirectsTo: location.href,
      redirectsTo: createUrl(this.fc.config.redirectTo || '', {
        orderId: lead.orderId || '',
      }),
      custom_order_sid: lead.sessionId,
      custom_order_auid: lead.auid || '',
      custom_order_paymentType: lead.paymentType || '',
      custom_order_paymentMethod: lead.paymentMethod || '',
      note:
        upsellKey !== undefined
          ? `${product?.isDownsell ? 'downsell' : 'upsell'}${upsellKey + 1}`
          : '',
      token: ppToken,
      paypalAccept: confirmed ? 1 : 0,
      payerId,
    };
    const promoFieldName = getPromoFieldName(this.upsellKey);
    if (promoFieldName) {
      upsellData[promoFieldName] = String(product.id);
    }

    return await Api.importPaypalUpsell(upsellData);
  };
}
